// Hato (animales). Ganadería → Hato. Pantalla principal del módulo.
import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Pencil, PawPrint, Plus, Scale, Truck } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import {
  Animal, AnimalFicha, Campo, ESTADOS_ANIMAL, Lote, Modal, PROCEDENCIAS, Potrero, Predio, Procedencia,
  RegistrarMovimientoModal, RegistrarPesajeModal, SEXOS, SexoAnimal, SortTh, estadoInfo, edadTexto, fieldCls,
  ganRpc, invalidateGanaderia, num, sexoLabel, tipoMovLabel, useSort,
} from './shared';

const vacio = {
  id: '', arete: '', nombre: '', especie: 'bovino', raza: '', sexo: 'H' as SexoAnimal,
  fecha_nacimiento: '', categoria: '', procedencia: 'nacido' as Procedencia, costo_inicial: '',
  predio_id: '', lote_id: '', potrero_id: '', madre_id: '', padre_id: '', observaciones: '',
};

export function Hato() {
  const qc = useQueryClient();
  const [estado, setEstado] = useState('');
  const [loteId, setLoteId] = useState('');
  const [potreroId, setPotreroId] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [fichaId, setFichaId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState(vacio);
  const [abierto, setAbierto] = useState(false);

  const { data: animales = [], isFetching } = useQuery<Animal[]>({
    queryKey: ['gan-animales', estado, loteId, potreroId, busqueda],
    queryFn: () => ganRpc<Animal[]>('gan_animal_listar', {
      _estado: estado || null, _lote: loteId || null, _potrero: potreroId || null, _busqueda: busqueda || null,
    }),
  });
  const { data: lotes = [] } = useQuery<Lote[]>({ queryKey: ['gan-lotes'], queryFn: () => ganRpc<Lote[]>('gan_lote_listar') });
  const { data: potreros = [] } = useQuery<Potrero[]>({ queryKey: ['gan-potreros'], queryFn: () => ganRpc<Potrero[]>('gan_potrero_listar') });
  const { data: predios = [] } = useQuery<Predio[]>({ queryKey: ['gan-predios'], queryFn: () => ganRpc<Predio[]>('gan_predio_listar') });
  // catálogo completo (sin filtros) para los selects de madre/padre en el alta
  const { data: todosAnimales = [] } = useQuery<Animal[]>({
    queryKey: ['gan-animales-todos'], queryFn: () => ganRpc<Animal[]>('gan_animal_listar'),
  });
  const madres = useMemo(() => todosAnimales.filter((a) => a.sexo === 'H'), [todosAnimales]);
  const padres = useMemo(() => todosAnimales.filter((a) => a.sexo === 'M'), [todosAnimales]);

  const abrir = (a?: Animal) => {
    if (a) {
      setEditId(a.id);
      setForm({
        id: a.id, arete: a.arete, nombre: a.nombre ?? '', especie: a.especie ?? 'bovino', raza: a.raza ?? '',
        sexo: a.sexo, fecha_nacimiento: a.fecha_nacimiento ?? '', categoria: a.categoria ?? '',
        procedencia: (a.procedencia ?? 'nacido') as Procedencia, costo_inicial: a.costo_inicial != null ? String(a.costo_inicial) : '',
        predio_id: a.predio_id ?? '', lote_id: a.lote_id ?? '', potrero_id: a.potrero_id ?? '',
        madre_id: a.madre_id ?? '', padre_id: a.padre_id ?? '', observaciones: a.observaciones ?? '',
      });
    } else { setEditId(null); setForm(vacio); }
    setAbierto(true);
  };

  const guardar = useMutation({
    mutationFn: () => ganRpc<Animal>('gan_animal_guardar', {
      _data: {
        id: editId || undefined, arete: form.arete, nombre: form.nombre || null, especie: form.especie,
        raza: form.raza || null, sexo: form.sexo, fecha_nacimiento: form.fecha_nacimiento || null,
        categoria: form.categoria || null, procedencia: form.procedencia,
        costo_inicial: form.costo_inicial === '' ? null : Number(form.costo_inicial),
        predio_id: form.predio_id || null, lote_id: form.lote_id || null, potrero_id: form.potrero_id || null,
        madre_id: form.madre_id || null, padre_id: form.padre_id || null, observaciones: form.observaciones || null,
      },
    }),
    onSuccess: () => { toast.success('Animal guardado'); setAbierto(false); invalidateGanaderia(qc); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const getVal = useCallback((r: Animal, key: string): string | number | null => {
    switch (key) {
      case 'arete': return r.arete;
      case 'nombre': return r.nombre;
      case 'categoria': return r.categoria;
      case 'sexo': return sexoLabel(r.sexo);
      case 'raza': return r.raza;
      case 'edad': return r.edad_meses;
      case 'estado': return estadoInfo(r.estado).label;
      case 'lote': return r.lote_nombre;
      case 'potrero': return r.potrero_nombre;
      default: return null;
    }
  }, []);
  const sort = useSort(animales, getVal);

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold flex items-center gap-2"><PawPrint className="w-5 h-5" /> Hato</h1>
        <Button onClick={() => abrir()}><Plus className="w-4 h-4 mr-1" /> Nuevo animal</Button>
      </div>

      <Card>
        <CardContent className="pt-4 flex flex-wrap gap-3 items-end">
          <Campo label="Estado" className="w-40">
            <select className={fieldCls} value={estado} onChange={(e) => setEstado(e.target.value)}>
              <option value="">Todos</option>
              {ESTADOS_ANIMAL.map((e) => <option key={e.v} value={e.v}>{e.label}</option>)}
            </select>
          </Campo>
          <Campo label="Lote" className="w-48">
            <select className={fieldCls} value={loteId} onChange={(e) => setLoteId(e.target.value)}>
              <option value="">Todos</option>
              {lotes.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Potrero" className="w-48">
            <select className={fieldCls} value={potreroId} onChange={(e) => setPotreroId(e.target.value)}>
              <option value="">Todos</option>
              {potreros.map((p) => <option key={p.id} value={p.id}>{p.predio_nombre} · {p.nombre}</option>)}
            </select>
          </Campo>
          <Campo label="Buscar (arete o nombre)" className="flex-1 min-w-[200px]">
            <input className={fieldCls} placeholder="Ej. 0021 o Lucero" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
          </Campo>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Animales ({animales.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-3 text-right">N°</th>
              <SortTh col="arete" sort={sort}>Arete</SortTh>
              <SortTh col="nombre" sort={sort}>Nombre</SortTh>
              <SortTh col="categoria" sort={sort}>Categoría</SortTh>
              <SortTh col="sexo" sort={sort}>Sexo</SortTh>
              <SortTh col="raza" sort={sort}>Raza</SortTh>
              <SortTh col="edad" sort={sort} align="right">Edad</SortTh>
              <SortTh col="estado" sort={sort}>Estado</SortTh>
              <SortTh col="lote" sort={sort}>Lote</SortTh>
              <SortTh col="potrero" sort={sort}>Potrero</SortTh>
            </tr></thead>
            <tbody>
              {sort.sorted.length === 0 && <tr><td colSpan={10} className="py-8 text-center text-muted-foreground">Sin animales registrados</td></tr>}
              {sort.sorted.map((a, i) => {
                const info = estadoInfo(a.estado);
                return (
                  <tr key={a.id} className="border-b last:border-0 hover:bg-muted/40 cursor-pointer" onClick={() => setFichaId(a.id)}>
                    <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                    <td className="py-2 pr-3 font-medium">{a.arete}</td>
                    <td className="py-2 pr-3">{a.nombre ?? '—'}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{a.categoria ?? '—'}</td>
                    <td className="py-2 pr-3">{sexoLabel(a.sexo)}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{a.raza ?? '—'}</td>
                    <td className="py-2 pr-3 text-right font-mono tabular-nums">{edadTexto(a.edad_meses)}</td>
                    <td className="py-2 pr-3"><span className={`rounded-full px-2 py-0.5 text-xs font-medium ${info.cls}`}>{info.label}</span></td>
                    <td className="py-2 pr-3 text-muted-foreground">{a.lote_nombre ?? '—'}</td>
                    <td className="py-2 pr-3 text-muted-foreground">{a.potrero_nombre ?? '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {abierto && (
        <Modal title={editId ? 'Editar animal' : 'Nuevo animal'} onClose={() => setAbierto(false)} wide>
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Arete *"><input className={fieldCls} value={form.arete} onChange={(e) => setForm((f) => ({ ...f, arete: e.target.value }))} /></Campo>
              <Campo label="Nombre"><input className={fieldCls} value={form.nombre} onChange={(e) => setForm((f) => ({ ...f, nombre: e.target.value }))} /></Campo>
              <Campo label="Especie"><input className={fieldCls} value={form.especie} onChange={(e) => setForm((f) => ({ ...f, especie: e.target.value }))} /></Campo>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Raza"><input className={fieldCls} value={form.raza} onChange={(e) => setForm((f) => ({ ...f, raza: e.target.value }))} /></Campo>
              <Campo label="Sexo *">
                <select className={fieldCls} value={form.sexo} onChange={(e) => setForm((f) => ({ ...f, sexo: e.target.value as SexoAnimal }))}>
                  {SEXOS.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                </select>
              </Campo>
              <Campo label="Fecha de nacimiento"><input type="date" className={fieldCls} value={form.fecha_nacimiento} onChange={(e) => setForm((f) => ({ ...f, fecha_nacimiento: e.target.value }))} /></Campo>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Categoría"><input className={fieldCls} placeholder="Ternero, novillo, vaca…" value={form.categoria} onChange={(e) => setForm((f) => ({ ...f, categoria: e.target.value }))} /></Campo>
              <Campo label="Procedencia">
                <select className={fieldCls} value={form.procedencia} onChange={(e) => setForm((f) => ({ ...f, procedencia: e.target.value as Procedencia }))}>
                  {PROCEDENCIAS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                </select>
              </Campo>
              <Campo label="Costo inicial ($)"><input type="number" min="0" step="0.01" className={fieldCls} value={form.costo_inicial} onChange={(e) => setForm((f) => ({ ...f, costo_inicial: e.target.value }))} /></Campo>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <Campo label="Predio">
                <select className={fieldCls} value={form.predio_id} onChange={(e) => setForm((f) => ({ ...f, predio_id: e.target.value }))}>
                  <option value="">— Selecciona —</option>
                  {predios.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Lote">
                <select className={fieldCls} value={form.lote_id} onChange={(e) => setForm((f) => ({ ...f, lote_id: e.target.value }))}>
                  <option value="">— Selecciona —</option>
                  {lotes.map((l) => <option key={l.id} value={l.id}>{l.nombre}</option>)}
                </select>
              </Campo>
              <Campo label="Potrero">
                <select className={fieldCls} value={form.potrero_id} onChange={(e) => setForm((f) => ({ ...f, potrero_id: e.target.value }))}>
                  <option value="">— Selecciona —</option>
                  {potreros.map((p) => <option key={p.id} value={p.id}>{p.predio_nombre} · {p.nombre}</option>)}
                </select>
              </Campo>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Campo label="Madre">
                <select className={fieldCls} value={form.madre_id} onChange={(e) => setForm((f) => ({ ...f, madre_id: e.target.value }))}>
                  <option value="">— Sin registrar —</option>
                  {madres.filter((m) => m.id !== editId).map((m) => <option key={m.id} value={m.id}>{m.arete}{m.nombre ? ` · ${m.nombre}` : ''}</option>)}
                </select>
              </Campo>
              <Campo label="Padre">
                <select className={fieldCls} value={form.padre_id} onChange={(e) => setForm((f) => ({ ...f, padre_id: e.target.value }))}>
                  <option value="">— Sin registrar —</option>
                  {padres.filter((p) => p.id !== editId).map((p) => <option key={p.id} value={p.id}>{p.arete}{p.nombre ? ` · ${p.nombre}` : ''}</option>)}
                </select>
              </Campo>
            </div>
            <Campo label="Observaciones"><textarea className={`${fieldCls} h-16`} value={form.observaciones} onChange={(e) => setForm((f) => ({ ...f, observaciones: e.target.value }))} /></Campo>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setAbierto(false)}>Cancelar</Button>
              <Button isLoading={guardar.isPending} disabled={!form.arete.trim() || !form.sexo} onClick={() => guardar.mutate()}>Guardar</Button>
            </div>
          </div>
        </Modal>
      )}

      {fichaId && (
        <FichaAnimal id={fichaId} onClose={() => setFichaId(null)} onEditar={(a) => { setFichaId(null); abrir(a); }} />
      )}
    </div>
  );
}

function FichaAnimal({ id, onClose, onEditar }: { id: string; onClose: () => void; onEditar: (a: Animal) => void }) {
  const qc = useQueryClient();
  const [modalPesaje, setModalPesaje] = useState(false);
  const [modalMovimiento, setModalMovimiento] = useState(false);

  const { data, isFetching, refetch } = useQuery<AnimalFicha>({
    queryKey: ['gan-ficha', id],
    queryFn: () => ganRpc<AnimalFicha>('gan_animal_ficha', { _id: id }),
    staleTime: 0,
    refetchOnMount: 'always',
  });

  const onMutado = () => { invalidateGanaderia(qc); void refetch(); };

  return (
    <Modal title={data ? `Ficha · ${data.animal.arete}${data.animal.nombre ? ` — ${data.animal.nombre}` : ''}` : 'Ficha del animal'} onClose={onClose} wide>
      {isFetching && !data ? (
        <div className="py-6 flex items-center justify-center gap-2 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /> Cargando…</div>
      ) : !data ? (
        <p className="text-sm text-muted-foreground">No se pudo cargar la ficha.</p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => onEditar(data.animal)}><Pencil className="w-4 h-4 mr-1" /> Editar</Button>
            <Button size="sm" variant="outline" onClick={() => setModalPesaje(true)}><Scale className="w-4 h-4 mr-1" /> Registrar pesaje</Button>
            <Button size="sm" variant="outline" onClick={() => setModalMovimiento(true)}><Truck className="w-4 h-4 mr-1" /> Registrar movimiento</Button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <Info label="Sexo" value={sexoLabel(data.animal.sexo)} />
            <Info label="Categoría" value={data.animal.categoria ?? '—'} />
            <Info label="Raza" value={data.animal.raza ?? '—'} />
            <Info label="Edad" value={edadTexto(data.animal.edad_meses)} />
            <Info label="Estado" value={estadoInfo(data.animal.estado).label} />
            <Info label="Lote" value={data.animal.lote_nombre ?? '—'} />
            <Info label="Potrero" value={data.animal.potrero_nombre ?? '—'} />
            <Info label="Predio" value={data.animal.predio_nombre ?? '—'} />
            <Info label="Madre" value={data.animal.madre_arete ?? '—'} />
            <Info label="Padre" value={data.animal.padre_arete ?? '—'} />
            <Info label="GDP (kg/día)" value={data.gdp != null ? num(data.gdp, 3) : '—'} />
          </div>
          {data.animal.observaciones && (
            <p className="text-sm text-muted-foreground border-t pt-2">{data.animal.observaciones}</p>
          )}

          <div>
            <h4 className="text-sm font-semibold mb-1">Pesajes</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Fecha</th>
                  <th className="py-2 pr-3 text-right">Peso (kg)</th><th className="py-2 pr-3">Tipo</th>
                </tr></thead>
                <tbody>
                  {data.pesajes.length === 0 && <tr><td colSpan={4} className="py-4 text-center text-muted-foreground">Sin pesajes registrados</td></tr>}
                  {data.pesajes.map((p, i) => (
                    <tr key={`${p.fecha}-${i}`} className="border-b last:border-0">
                      <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-3">{p.fecha}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{num(p.peso_kg, 1)}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{p.tipo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold mb-1">Movimientos</h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-2 pr-3 text-right">N°</th><th className="py-2 pr-3">Fecha</th><th className="py-2 pr-3">Tipo</th>
                  <th className="py-2 pr-3">Origen</th><th className="py-2 pr-3">Destino</th>
                  <th className="py-2 pr-3 text-right">Peso (kg)</th><th className="py-2 pr-3 text-right">Precio</th><th className="py-2 pr-3">Motivo</th>
                </tr></thead>
                <tbody>
                  {data.movimientos.length === 0 && <tr><td colSpan={8} className="py-4 text-center text-muted-foreground">Sin movimientos registrados</td></tr>}
                  {data.movimientos.map((m, i) => (
                    <tr key={`${m.fecha}-${i}`} className="border-b last:border-0">
                      <td className="py-2 pr-3 text-right text-muted-foreground">{i + 1}</td>
                      <td className="py-2 pr-3">{m.fecha}</td>
                      <td className="py-2 pr-3">{tipoMovLabel(m.tipo)}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{m.potrero_origen ?? '—'}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{m.potrero_destino ?? '—'}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{m.peso_kg != null ? num(m.peso_kg, 1) : '—'}</td>
                      <td className="py-2 pr-3 text-right font-mono tabular-nums">{m.precio != null ? num(m.precio, 2) : '—'}</td>
                      <td className="py-2 pr-3 text-muted-foreground">{m.motivo ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {modalPesaje && data && (
        <RegistrarPesajeModal animal={data.animal} onClose={() => setModalPesaje(false)} onSaved={onMutado} />
      )}
      {modalMovimiento && data && (
        <RegistrarMovimientoModal animal={data.animal} onClose={() => setModalMovimiento(false)} onSaved={onMutado} />
      )}
    </Modal>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="font-medium">{value}</div>
    </div>
  );
}
